// AI Signals Table Component
import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
  Brain,
  Clock,
  DollarSign,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  MoreHorizontal,
  SortAsc,
  SortDesc,
  RefreshCw,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AISignal, SignalFilters } from "@/lib/ai-signals-api";
import { useSignalExecution } from "@/hooks/use-ai-signals";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import { SignalCard } from "./SignalCard";

interface SignalTableProps {
  signals: AISignal[];
  totalCount: number;
  page: number;
  perPage: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
  onRefresh?: () => void;
  loading?: boolean;
  filters: SignalFilters;
  onFiltersChange: (filters: SignalFilters) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

export function SignalTable({
  signals,
  totalCount,
  page,
  perPage,
  hasNext,
  hasPrev,
  onPageChange,
  onRefresh,
  loading = false,
  filters,
  onFiltersChange,
  sortBy,
  sortOrder,
  onSortChange
}: SignalTableProps) {
  const [searchTerm, setSearchTerm] = useState(filters.symbol || "");
  const [selectedSignal, setSelectedSignal] = useState<AISignal | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const { executeSignal, cancelSignal } = useSignalExecution();
  const { publicKey } = useWallet();

  const handleFilterChange = (key: keyof SignalFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value || undefined });
  };

  const clearFilters = () => {
    setSearchTerm("");
    onFiltersChange({});
  };

  const handleExecute = async (signalId: string) => {
    try {
      const result = await executeSignal(signalId, publicKey || undefined);
      if (result.success) {
        toast({
          title: "Signal Executed",
          description: "Successfully executed the AI signal",
        });
        onRefresh?.();
      } else {
        toast({
          title: "Execution Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error executing signal:', error);
    }
  };

  const handleCancel = async (signalId: string) => {
    try {
      const result = await cancelSignal(signalId);
      if (result.success) {
        toast({
          title: "Signal Cancelled",
          description: "Successfully cancelled the AI signal",
        });
        onRefresh?.();
      } else {
        toast({
          title: "Cancellation Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error cancelling signal:', error);
    }
  };

  useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        onFiltersChange({ ...filters, symbol: searchTerm });
      } else {
        onFiltersChange({ ...filters, symbol: undefined });
      }
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filters, onFiltersChange]);

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />;
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Clicking the same column, toggle the order
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      onSortChange(column, newOrder);
    } else {
      // Clicking a new column, default to 'asc'
      onSortChange(column, 'asc');
    }
  };

  const getSignalIcon = (signalType: string) => {
    switch (signalType) {
      case 'BUY':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'SELL':
        return <TrendingDown className="w-4 h-4 text-destructive" />;
      case 'HOLD':
        return <Minus className="w-4 h-4 text-warning" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case 'HIGH':
        return "text-success";
      case 'MEDIUM':
        return "text-warning";
      case 'LOW':
        return "text-destructive";
      default:
        return "text-muted-foreground";
    }
  };

  const getRiskColor = (risk: number) => {
    if (risk <= 30) return "text-success";
    if (risk <= 60) return "text-warning";
    return "text-destructive";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge variant="outline" className="text-success border-success/20">
            <Activity className="w-3 h-3 mr-1" />
            Active
          </Badge>
        );
      case 'EXECUTED':
        return (
          <Badge variant="outline" className="text-success border-success/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Executed
          </Badge>
        );
      case 'EXPIRED':
        return (
          <Badge variant="outline" className="text-muted-foreground">
            <XCircle className="w-3 h-3 mr-1" />
            Expired
          </Badge>
        );
      case 'CANCELLED':
        return (
          <Badge variant="outline" className="text-destructive border-destructive/20">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-card rounded-lg border border-border">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search symbols..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <Select 
            value={filters.signal_type || ""} 
            onValueChange={(value) => handleFilterChange('signal_type', value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Signal Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Types</SelectItem>
              <SelectItem value="BUY">Buy</SelectItem>
              <SelectItem value="SELL">Sell</SelectItem>
              <SelectItem value="HOLD">Hold</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={filters.confidence_level || ""} 
            onValueChange={(value) => handleFilterChange('confidence_level', value)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Confidence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Levels</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={filters.status || ""} 
            onValueChange={(value) => handleFilterChange('status', value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="EXECUTED">Executed</SelectItem>
              <SelectItem value="EXPIRED">Expired</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={clearFilters} size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Clear
          </Button>

          {onRefresh && (
            <Button variant="outline" onClick={onRefresh} size="sm" disabled={loading}>
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <ScrollArea className="w-full">
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('symbol')}
              >
                <div className="flex items-center gap-2">
                  Symbol
                  {getSortIcon('symbol')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('signal_type')}
              >
                <div className="flex items-center gap-2">
                  Type
                  {getSortIcon('signal_type')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('confidence_score')}
              >
                <div className="flex items-center gap-2">
                  Confidence
                  {getSortIcon('confidence_score')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('risk_score')}
              >
                <div className="flex items-center gap-2">
                  Risk
                  {getSortIcon('risk_score')}
                </div>
              </TableHead>
              <TableHead>Current Price</TableHead>
              <TableHead>Price Target</TableHead>
              <TableHead>Model</TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-2">
                  Created
                  {getSortIcon('created_at')}
                </div>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {signals.map((signal) => (
              <TableRow key={signal.id} className="hover:bg-muted/50">
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getSignalIcon(signal.signal_type)}
                    <span className="font-mono font-semibold">{signal.symbol}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={signal.signal_type === 'BUY' ? 'default' : signal.signal_type === 'SELL' ? 'destructive' : 'secondary'}>
                    {signal.signal_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={cn("font-mono", getConfidenceColor(signal.confidence_level))}>
                      {(signal.confidence_score * 100).toFixed(1)}%
                    </div>
                    <Badge variant="outline" className={getConfidenceColor(signal.confidence_level)}>
                      {signal.confidence_level}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={cn("font-mono", getRiskColor(signal.risk_score))}>
                    {signal.risk_score}/100
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-mono">${signal.current_price.toFixed(6)}</span>
                </TableCell>
                <TableCell>
                  {signal.price_target ? (
                    <span className="font-mono text-primary">${signal.price_target.toFixed(6)}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Brain className="w-3 h-3" />
                    <span className="text-sm">{signal.model_name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span className="text-sm">
                      {new Date(signal.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(signal.status)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>AI Signal Details - {signal.symbol}</DialogTitle>
                        </DialogHeader>
                        <SignalCard signal={signal} compact />
                      </DialogContent>
                    </Dialog>
                    
                    {signal.status === 'ACTIVE' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExecute(signal.id)}
                        >
                          <Play className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCancel(signal.id)}
                        >
                          <Pause className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </ScrollArea>

        {signals.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Activity className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Signals Found</h3>
            <p className="text-muted-foreground max-w-sm">
              {Object.keys(filters).length > 0
                ? "Try adjusting your filters to see more results."
                : "AI signals will appear here when generated."
              }
            </p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading signals...</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, totalCount)} of {totalCount} results
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={!hasPrev || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNext || loading}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}